import {
  Box,
  Checkbox,
  FormControlLabel,
  styled,
  Typography,
} from "@material-ui/core";
import { EpocusDeviceFormFactor } from "elephant-web/gql";
import { useMemo } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import { SettingsCard, SettingsCardLabel } from "../../_lib/settings-card";
import type {
  EpocusDeviceCatalogueQuery,
  EpocusProgramConfigQuery,
} from "./__generated__/program-config-operations.gql";
import {
  useEpocusDeviceCatalogueQuery,
  useEpocusSetProgramEnabledDeviceIdsMutation,
} from "./__generated__/program-config-operations.gql";
import { SelectionSaveRow } from "./selection-save-row";
import { useProgramSelection } from "./use-program-selection";

type CatalogueDevice =
  EpocusDeviceCatalogueQuery["epocusDeviceCatalogue"][number];

const FORM_FACTOR_LABELS: Record<EpocusDeviceFormFactor, string> = {
  [EpocusDeviceFormFactor.Cart]: "Cart",
  [EpocusDeviceFormFactor.Handheld]: "Handheld",
  [EpocusDeviceFormFactor.Portable]: "Portable",
};

export const selectEnabledDeviceIds = (
  catalogue: readonly CatalogueDevice[],
  enabled: readonly string[],
): readonly string[] => {
  const everything = catalogue.map((device) => device.id);
  if (enabled.length === 0) return everything;

  const enabledSet = new Set(enabled);
  const narrowed = everything.filter((id) => enabledSet.has(id));

  return narrowed.length === 0 ? everything : narrowed;
};

const OptionList = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const OptionRow = styled(FormControlLabel)({
  margin: 0,
  minHeight: 52,
  alignItems: "center",
});

export const EnabledDevicesCard: FC<{
  programId: string;
  config: EpocusProgramConfigQuery["programConfig"] | undefined;
}> = ({ programId, config }) => {
  const { data, error: catalogueError } = useEpocusDeviceCatalogueQuery({
    fetchPolicy: "cache-and-network",
  });
  const [setEnabledDeviceIds] = useEpocusSetProgramEnabledDeviceIdsMutation();

  const catalogue = data?.epocusDeviceCatalogue;

  const configured = useMemo(
    () =>
      catalogue && config !== undefined
        ? selectEnabledDeviceIds(catalogue, config?.enabledDeviceIds ?? [])
        : null,
    [catalogue, config],
  );

  const selection = useProgramSelection<string>(
    programId,
    configured,
    (values) =>
      setEnabledDeviceIds({
        variables: {
          input: {
            programId,
            enabledDeviceIds:
              catalogue && values.length === catalogue.length ? [] : values,
          },
        },
      }),
  );

  const { selected, saving, toggle } = selection;

  return (
    <SettingsCard>
      <SettingsCardLabel>Enabled devices</SettingsCardLabel>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        Only the devices you enable appear when someone starts a new encounter.
        Encounters already recorded against a device you turn off keep naming
        it.
      </Typography>

      {!selected ? (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          {catalogueError
            ? "Couldn't load the device catalogue."
            : "Loading devices…"}
        </Typography>
      ) : (
        <>
          <OptionList>
            {catalogue?.map((device) => (
              <OptionRow
                key={device.id}
                control={
                  <Checkbox
                    color="primary"
                    checked={selected.includes(device.id)}
                    disabled={saving}
                    inputProps={{ "aria-label": device.model }}
                    onChange={() => toggle(device.id)}
                  />
                }
                label={
                  <Box>
                    <Typography
                      style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}
                    >
                      {device.model}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: rem(12),
                        color: EPOCUS_TEXT_SECONDARY,
                      }}
                    >
                      {`${device.vendor} · ${FORM_FACTOR_LABELS[device.formFactor]}`}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </OptionList>

          <SelectionSaveRow
            selection={selection}
            saveLabel="Save devices"
            emptyWarning="Enable at least one device. Saving none would turn every device back on."
          />
        </>
      )}
    </SettingsCard>
  );
};
